import * as React from 'react'
import { FormikHelpers } from 'formik'

import { AsyncStatus, UpdateUsernameProps } from '@ally/transmitigator'
import { Button, Space, TextBody } from '@ally/metronome-ui'
import { Form, Forms, HelperRuleProps, useFormsContext } from '@ally/forms'
import { LoginHeading } from '@ally/ally-login'

import { useTransmit } from '../../TransmitContext'
import {
  CIAMErrorBanner,
  InputWrapper,
  SharedNewUsername,
  StyledTextBody,
} from '../Shared'
import { InputFieldRuleHelper } from '../InputFieldRuleHelper'
import { useHostServices } from '../../HostServices'

interface UpdateUsernameFormProps {
  formId: string
  status: AsyncStatus
}

const UpdateUsernameForm: React.FC<UpdateUsernameFormProps> = ({
  formId,
  status,
}) => {
  const specialCharValidation = (newUsername: string): boolean =>
    Boolean(newUsername && !/[*^()[\]\\|/]/g.test(newUsername))
  const minCharValidation = (newUsername: string): boolean =>
    /[a-zA-Z]/.test(newUsername)
  const charRangeAndSpaceValidation = (newUsername: string): boolean =>
    Boolean(
      newUsername &&
        newUsername.length > 3 &&
        newUsername.length < 29 &&
        !/\s/g.test(newUsername),
    )

  const newUsernameHelperDirections: HelperRuleProps[] = [
    {
      text: 'Your username cannot contain ^ * () \\ |',
      isValid: specialCharValidation,
    },
    {
      text: 'At least one letter',
      isValid: minCharValidation,
    },
    {
      text: '4-28 characters, no spaces',
      isValid: charRangeAndSpaceValidation,
    },
  ]

  const { values } = useFormsContext()

  return (
    <Form id={formId}>
      <InputWrapper mb="md">
        <SharedNewUsername label="New username" name="newUsername" />
      </InputWrapper>

      <InputFieldRuleHelper
        directions={newUsernameHelperDirections}
        fieldInput={values.newUsername}
        id="password-hint-helper"
        name="passwordHintRuleHelper"
      />
      <Space mt="lg">
        <Button
          allytmln="updateAndContinue"
          text="Update and Continue"
          loading={status === 'Loading'}
          type="submit"
          variant="primary"
        />
      </Space>
    </Form>
  )
}

interface UpdateUsernameFormState {
  newUsername: string
}

const UpdateUsernameContent: React.FC<UpdateUsernameProps> = ({
  onComplete,
  status,
  error,
  username,
}) => {
  const formId = 'update-username-form'
  const initialValues: UpdateUsernameFormState = {
    newUsername: '',
  }
  const handleSubmit = (
    formValues: UpdateUsernameFormState,
    { setSubmitting }: FormikHelpers<UpdateUsernameFormState>,
  ): void => {
    onComplete({
      newUsername: formValues.newUsername,
    })

    setSubmitting(false)
  }

  const { analytics } = useHostServices()

  React.useEffect(() => {
    analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Update Username',
    })
  }, [])

  return (
    <>
      <LoginHeading title="Update your username" />

      <Space mb="lg">
        <StyledTextBody size="sm" tag="p">
          We&apos;re making some changes for a more streamlined experience, and
          we need you to choose a new username. Your password doesn&apos;t need
          to change right now.
        </StyledTextBody>
      </Space>

      <CIAMErrorBanner error={error} />

      <Space mb="lg">
        <StyledTextBody size="sm" tag="p" weight="bold">
          Current username:{' '}
          <TextBody size="sm" tag="span" weight="normal">
            {username}
          </TextBody>
        </StyledTextBody>
      </Space>

      <Forms
        formId={formId}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <UpdateUsernameForm formId={formId} status={status} />
      </Forms>
    </>
  )
}

export const UpdateUsername: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'UpdateUsername') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <UpdateUsernameContent {...activeScreen.payload} />
}
